/**
 * Distance Measurement Controller - handles distance measurement operations
 * Manages distance measurement mode on the map
 */
import {Subject} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {BaseDrawController} from './base-draw.controller';

export class DrawDistanceController extends BaseDrawController {
  private isDistanceMeasurementActive: boolean = false;
  private distanceMeasurementPoints: any[] = [];
  private distanceMeasurementLine: any = null;
  private distanceMeasurementMarkers: any[] = [];
  private distanceMeasurementLabels: any[] = [];

  // Event handlers stored for cleanup
  private distanceClickHandler: any = null;

  // Callback to check if other drawing modes are active
  private isOtherDrawingModeActive: (() => boolean) | null = null;

  private translateService: TranslateService;

  // Event emitter - distance controller doesn't create persistent shapes
  public shapeCreated$ = new Subject<any>();

  constructor(mapInstance: any, translateService: TranslateService) {
    super(mapInstance);
    this.translateService = translateService;
  }

  /**
   * Set callback to check if other drawing modes are active
   */
  public setIsOtherDrawingModeActiveCallback(callback: () => boolean): void {
    this.isOtherDrawingModeActive = callback;
  }

  /**
   * Enable distance measurement mode
   */
  public enable(): void {
    if (this.isDistanceMeasurementActive) return;

    this.isDistanceMeasurementActive = true;
    this.distanceMeasurementPoints = [];

    // Change cursor
    this.enableDrawingMode('distance-measurement-active');

    // Add click handler and mouse move handler for real-time preview
    this.distanceClickHandler = (e: any) => this.handleDistanceMeasurementClick(e);
    this.mapMouseMoveHandler = (e: any) => this.handleDistanceMeasurementMouseMove(e);

    this.map.on('click', this.distanceClickHandler);
    this.map.on('mousemove', this.mapMouseMoveHandler);
  }

  /**
   * Disable distance measurement mode
   */
  public disable(): void {
    if (!this.isDistanceMeasurementActive) return;

    this.isDistanceMeasurementActive = false;

    // Remove all measurement layers
    this.clearDistanceMeasurement();

    // Reset cursor
    this.disableDrawingMode('distance-measurement-active');

    // Remove click handler and mouse move handler
    this.removeMapEventListeners([
      {event: 'click', handler: this.distanceClickHandler},
      {event: 'mousemove', handler: this.mapMouseMoveHandler}
    ]);
    this.distanceClickHandler = null;
    this.mapMouseMoveHandler = null;
  }

  /**
   * Check if distance measurement is active
   */
  public override isActive(): boolean {
    return this.isDistanceMeasurementActive;
  }

  /**
   * Handle mouse move during distance measurement for real-time preview
   */
  private handleDistanceMeasurementMouseMove(e: any): void {
    if (!this.isDistanceMeasurementActive) return;

    // Only show preview if we have exactly 1 point
    if (this.distanceMeasurementPoints.length !== 1) return;

    const L = (window as any).L;
    const point1 = this.distanceMeasurementPoints[0];
    const currentPoint = e.latlng;

    // Calculate distance in real-time
    const distance = this.calculateDistance(
      point1.lat, point1.lng,
      currentPoint.lat, currentPoint.lng
    );

    // Remove old preview line if exists
    if (this.distanceMeasurementLine) {
      this.map.removeLayer(this.distanceMeasurementLine);
    }

    // Draw preview line
    this.distanceMeasurementLine = L.polyline([point1, currentPoint], {
      color: '#FF5722',
      weight: 3,
      opacity: 0.6,
      dashArray: '10, 5'
    }).addTo(this.map);

    // Calculate midpoint for distance label
    const midLat = (point1.lat + currentPoint.lat) / 2;
    const midLng = (point1.lng + currentPoint.lng) / 2;

    // Remove old preview label if exists
    if (this.distanceMeasurementLabels.length > 0) {
      this.distanceMeasurementLabels.forEach(label => this.map.removeLayer(label));
      this.distanceMeasurementLabels = [];
    }

    // Create preview distance label
    const distanceLabelContent = `
      <div style="
        background: #FF5722;
        color: white;
        padding: 6px 12px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        font-family: Arial, sans-serif;
        font-size: 14px;
        font-weight: bold;
        white-space: nowrap;
        text-align: center;
      ">
        ${distance.toFixed(2)} ${this.translateService.instant('km')}
      </div>
    `;

    const distanceLabelIcon = L.divIcon({
      className: 'distance-measurement-label',
      html: distanceLabelContent,
      iconSize: [100, 30],
      iconAnchor: [50, 15]
    });

    const distanceLabel = L.marker([midLat, midLng], {
      icon: distanceLabelIcon,
      interactive: false
    }).addTo(this.map);

    this.distanceMeasurementLabels.push(distanceLabel);
  }

  /**
   * Handle click during distance measurement
   */
  private handleDistanceMeasurementClick(e: any): void {
    if (!this.isDistanceMeasurementActive) return;

    // Don't handle clicks if other drawing modes are active
    if (this.isOtherDrawingModeActive && this.isOtherDrawingModeActive()) {
      return;
    }

    const L = (window as any).L;

    // Stop event propagation to prevent polygon clicks
    if (e.originalEvent) {
      e.originalEvent.stopPropagation();
    }

    // If we already have 2 points, reset and start new measurement
    if (this.distanceMeasurementPoints.length >= 2) {
      this.clearDistanceMeasurement();
    }

    // Add point
    this.distanceMeasurementPoints.push(e.latlng);

    // Create marker for the point with higher z-index
    const markerIcon = L.divIcon({
      className: 'distance-measurement-marker',
      html: `<div style="
        width: 12px;
        height: 12px;
        background: #FF5722;
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [12, 12],
      iconAnchor: [6, 6]
    });

    const marker = L.marker(e.latlng, {
      icon: markerIcon,
      zIndexOffset: 10000
    }).addTo(this.map);
    this.distanceMeasurementMarkers.push(marker);

    // If we have 2 points, draw the final line and show distance
    if (this.distanceMeasurementPoints.length === 2) {
      this.drawDistanceLine();
    }
  }

  /**
   * Draw line between two points and display distance
   */
  private drawDistanceLine(): void {
    if (this.distanceMeasurementPoints.length !== 2) return;

    const L = (window as any).L;
    const point1 = this.distanceMeasurementPoints[0];
    const point2 = this.distanceMeasurementPoints[1];

    // Calculate distance in kilometers using Haversine formula
    const distance = this.calculateDistance(
      point1.lat, point1.lng,
      point2.lat, point2.lng
    );

    // Remove old line if exists
    if (this.distanceMeasurementLine) {
      this.map.removeLayer(this.distanceMeasurementLine);
    }

    // Draw final line with solid style
    this.distanceMeasurementLine = L.polyline([point1, point2], {
      color: '#FF5722',
      weight: 3,
      opacity: 0.9,
      dashArray: '10, 5'
    }).addTo(this.map);

    // Calculate midpoint for distance label
    const midLat = (point1.lat + point2.lat) / 2;
    const midLng = (point1.lng + point2.lng) / 2;

    // Remove old labels if exists
    if (this.distanceMeasurementLabels.length > 0) {
      this.distanceMeasurementLabels.forEach(label => this.map.removeLayer(label));
      this.distanceMeasurementLabels = [];
    }

    // Label for distance at center of line
    const distanceLabelContent = `
      <div style="
        background: #FF5722;
        color: white;
        padding: 6px 12px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        font-family: Arial, sans-serif;
        font-size: 14px;
        font-weight: bold;
        white-space: nowrap;
        text-align: center;
      ">
        ${distance.toFixed(2)} ${this.translateService.instant('km')}
      </div>
    `;

    const distanceLabelIcon = L.divIcon({
      className: 'distance-measurement-label',
      html: distanceLabelContent,
      iconSize: [100, 30],
      iconAnchor: [50, 15]
    });

    const distanceLabel = L.marker([midLat, midLng], {
      icon: distanceLabelIcon,
      interactive: false,
      zIndexOffset: 10000
    }).addTo(this.map);

    this.distanceMeasurementLabels.push(distanceLabel);

    // Label for Point 1 coordinates
    const point1LabelContent = `
      <div style="
        background: white;
        padding: 4px 8px;
        border-radius: 3px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        font-family: Arial, sans-serif;
        font-size: 11px;
        white-space: nowrap;
        border-left: 3px solid #FF5722;
      ">
        <div style="color: #666; font-weight: bold; margin-bottom: 2px;">
        ${this.translateService.instant('Point')} 1
        </div>
        <div style="color: #333;">${point1.lat.toFixed(6)}</div>
        <div style="color: #333;">${point1.lng.toFixed(6)}</div>
      </div>
    `;

    const point1LabelIcon = L.divIcon({
      className: 'distance-point-label',
      html: point1LabelContent,
      iconSize: [120, 60],
      iconAnchor: [-15, 30]
    });

    const point1Label = L.marker([point1.lat, point1.lng], {
      icon: point1LabelIcon,
      interactive: false,
      zIndexOffset: 10000
    }).addTo(this.map);

    this.distanceMeasurementLabels.push(point1Label);

    // Label for Point 2 coordinates
    const point2LabelContent = `
      <div style="
        background: white;
        padding: 4px 8px;
        border-radius: 3px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        font-family: Arial, sans-serif;
        font-size: 11px;
        white-space: nowrap;
        border-left: 3px solid #FF5722;
      ">
        <div style="color: #666; font-weight: bold; margin-bottom: 2px;">${this.translateService.instant('Point')}  2</div>
        <div style="color: #333;">${point2.lat.toFixed(6)}</div>
        <div style="color: #333;">${point2.lng.toFixed(6)}</div>
      </div>
    `;

    const point2LabelIcon = L.divIcon({
      className: 'distance-point-label',
      html: point2LabelContent,
      iconSize: [120, 60],
      iconAnchor: [-15, 30]
    });

    const point2Label = L.marker([point2.lat, point2.lng], {
      icon: point2LabelIcon,
      interactive: false,
      zIndexOffset: 10000
    }).addTo(this.map);

    this.distanceMeasurementLabels.push(point2Label);
  }

  /**
   * Calculate distance between two points using Haversine formula
   * Returns distance in kilometers
   */
  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Clear all distance measurement layers
   */
  private clearDistanceMeasurement(): void {
    // Remove markers
    this.distanceMeasurementMarkers.forEach(marker => {
      this.map.removeLayer(marker);
    });
    this.distanceMeasurementMarkers = [];

    // Remove line
    if (this.distanceMeasurementLine) {
      this.map.removeLayer(this.distanceMeasurementLine);
      this.distanceMeasurementLine = null;
    }

    // Remove labels
    this.distanceMeasurementLabels.forEach(label => {
      this.map.removeLayer(label);
    });
    this.distanceMeasurementLabels = [];

    // Reset points
    this.distanceMeasurementPoints = [];
  }

  /**
   * Clear all distance measurements (public method)
   */
  public clearAllMeasurements(): void {
    this.clearDistanceMeasurement();
  }
}

/**
 * Polygon Drawing Controller - handles polygon drawing operations
 * Manages polyline drawing mode on the map
 */
